import { useState, useCallback } from 'react';

const useForm = (
  {
    initialValues = {},
    onSubmit,
    validator = () => {},
    validateOnChange = true,
    validateOnBlur = false,
    onInvalidSubmit = () => {}
  },
  ...dependencies
) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [blurredFields, setBlurredFields] = useState({});
  const [touched, setTouched] = useState(false);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      const newErrors = validator(values) || {};
      const valid = !Object.values(newErrors)
        .filter(error => !!error)
        .flat().length;
      if (valid) {
        onSubmit(values);
      } else {
        setErrors(newErrors);
        onInvalidSubmit();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, setErrors, validator, onSubmit, ...dependencies]
  );

  const runValidations = useCallback(
    (newValues, key, extraErrors) => {
      const validations = validator(newValues) || {};
      if (key) {
        const err = validations[key] || extraErrors;
        setErrors({ ...errors, [key]: err });
      } else {
        setErrors(validator(newValues));
      }
    },
    [validator, errors, setErrors]
  );

  const handleValueChange = useCallback(
    (key, value, extraErrors) => {
      const newValues = {
        ...values,
        [key]: value
      };
      setValues(newValues);
      if (validateOnChange && touched[key]) {
        runValidations(newValues, key, extraErrors);
      }
    },
    [values, touched, setValues, runValidations, validateOnChange]
  );

  const handleBlur = useCallback(
    (key, extraErrors) => {
      setBlurredFields({
        ...blurredFields,
        [key]: true
      });
      if (validateOnBlur) runValidations(values, key, extraErrors);
      setTouched({ ...touched, [key]: true });
    },
    [blurredFields, setBlurredFields, runValidations, values, validateOnBlur, setTouched, touched]
  );

  return {
    values,
    setValues,
    errors,
    setErrors,
    blurredFields,
    setBlurredFields,
    handleValueChange,
    handleSubmit,
    handleBlur,
    touched
  };
};

export default useForm;
